import { makeAutoObservable, runInAction } from 'mobx'
import { api } from '@/shared/api/endpoints'
import { explain } from '@/shared/lib/errors'
import type { AuthStore } from '@/stores/AuthStore'
import type { UiStore } from '@/stores/UiStore'

/**
 * Минимальная длина пароля. Ровно та же, что на бекенде
 * (`auth.py`, `len(password) < 8`). Разойдясь, фронт либо
 * пропустит пароль, который сервер отвергнет, либо не даст задать
 * тот, который сервер принял бы, — и человек будет думать,
 * что форма сломана.
 */
export const MIN_PASSWORD = 8

/** Шаги подключения второго фактора. */
export type TwoFactorStep =
  | { kind: 'idle' }
  | { kind: 'scan'; secret: string; uri: string }
  | { kind: 'codes'; codes: string[] }
  | { kind: 'disable' }

/** Чем подтверждаем смену: кодом из приложения или письмом. */
export type ConfirmMethod = 'totp' | 'email'

/**
 * Настройки аккаунта: почта, пароль и второй фактор.
 *
 * Все три живут диалогами из меню профиля — отдельной страницы
 * настроек в макете нет. Смена почты и смена пароля устроены
 * одинаково: форма, потом код. Текущий пароль не спрашивается
 * нигде: подтверждение — код на ТЕКУЩУЮ почту или из приложения,
 * и для угнанной сессии это та же преграда, а человеку, который
 * пароль забыл, но в кабинет ещё входит, не нужно восстановление.
 */
export class SettingsStore {
  // ---- пароль ----
  passwordStep: 'idle' | 'form' | 'code' = 'idle'
  next = ''
  repeat = ''
  passwordCode = ''
  /** Чем подтверждаем — знаем только после ответа бекенда. */
  passwordMethod: ConfirmMethod | null = null
  passwordErrors: { next?: string; repeat?: string; code?: string } = {}
  passwordBusy = false

  // ---- смена почты ----
  emailStep: 'idle' | 'form' | 'code' = 'idle'
  newEmail = ''
  emailCode = ''
  emailErrors: { email?: string; code?: string } = {}
  emailBusy = false

  // ---- часовой пояс ----
  timezone = ''
  timezoneBusy = false

  // ---- второй фактор ----
  twoFactor: TwoFactorStep = { kind: 'idle' }
  code = ''
  codeError: string | null = null
  twoFactorBusy = false
  /** Человек подтвердил, что сохранил резервные коды. */
  codesSaved = false

  private readonly auth: AuthStore
  private readonly ui: UiStore

  constructor(auth: AuthStore, ui: UiStore) {
    this.auth = auth
    this.ui = ui
    makeAutoObservable<this, 'auth' | 'ui'>(this, { auth: false, ui: false })
  }

  // ================= пароль =================

  openPassword(): void {
    this.passwordStep = 'form'
    this.next = ''
    this.repeat = ''
    this.passwordCode = ''
    this.passwordMethod = null
    this.passwordErrors = {}
  }

  closePassword(): void {
    if (this.passwordBusy) return
    this.passwordStep = 'idle'
    this.passwordErrors = {}
  }

  patchPassword(part: Partial<{ next: string; repeat: string; passwordCode: string }>): void {
    Object.assign(this, part)
    this.passwordErrors = {}
  }

  /**
   * Шаг 1: заказать смену. Бекенд отвечает, чем подтверждать.
   *
   * Пароль до подтверждения нигде в аккаунте не сохраняется: пока код
   * не введён, вход старым паролем работает. Иначе угнанная сессия
   * выбивала бы владельца одной формой без всякого подтверждения.
   */
  async requestPassword(): Promise<void> {
    if (this.passwordBusy) return
    const errors: typeof this.passwordErrors = {}
    if (this.next.length < MIN_PASSWORD) errors.next = `Не короче ${MIN_PASSWORD} символов`
    if (this.repeat !== this.next) errors.repeat = 'Пароли не совпадают'
    this.passwordErrors = errors
    if (Object.keys(errors).length > 0) return

    this.passwordBusy = true
    try {
      const { method } = await api.account.requestPasswordChange(this.next)
      runInAction(() => {
        this.passwordMethod = method
        this.passwordStep = 'code'
        this.passwordCode = ''
      })
    } catch (cause) {
      const message = explain(cause)
      runInAction(() => { this.passwordErrors = { next: message } })
    } finally {
      runInAction(() => { this.passwordBusy = false })
    }
  }

  /**
   * Шаг 2: подтверждение. Возвращает true, если пароль сменился.
   *
   * После смены бекенд гасит ВСЕ токены, включая тот, которым сделан
   * этот запрос: смена пароля — ровно тот момент, когда чужая сессия
   * не должна её пережить. Экран обязан отправить человека на вход
   * сам, а не оставить его тыкать в кнопки, каждая из которых
   * ответит «сессия истекла».
   */
  async confirmPassword(): Promise<boolean> {
    if (this.passwordBusy) return false
    if (this.passwordCode.trim() === '') {
      this.passwordErrors = { code: 'Введите код подтверждения' }
      return false
    }
    this.passwordBusy = true
    try {
      await api.account.confirmPasswordChange(this.passwordCode.trim())
      runInAction(() => { this.passwordStep = 'idle' })
      this.ui.success('Пароль изменён. Войдите заново')
      return true
    } catch (cause) {
      const message = explain(cause)
      runInAction(() => { this.passwordErrors = { code: message } })
      return false
    } finally {
      runInAction(() => { this.passwordBusy = false })
    }
  }

  // ================= смена почты =================

  /**
   * Смена почты в два шага. В форме одно поле — по макету.
   *
   * Почта — ключ восстановления пароля: подменивший её получает
   * аккаунт целиком, не зная пароля, — достаточно нажать «забыли
   * пароль». Вся защита в том, КУДА уходит код: на текущий адрес,
   * а не на новый. Спрашивать сверх этого текущий пароль макет
   * не просит, и бекенд больше не требует.
   */
  openEmail(): void {
    this.emailStep = 'form'
    this.newEmail = ''
    this.emailCode = ''
    this.emailErrors = {}
  }

  closeEmail(): void {
    if (this.emailBusy) return
    this.emailStep = 'idle'
    this.emailErrors = {}
  }

  patchEmail(part: Partial<{ newEmail: string; emailCode: string }>): void {
    Object.assign(this, part)
    this.emailErrors = {}
  }

  get twoFactorOn(): boolean {
    return this.auth.user?.['2fa_enabled'] === true
  }

  /** Почта, на которую придёт код, — текущая. Её же показываем в окне. */
  get currentEmail(): string {
    return this.auth.user?.email ?? ''
  }

  async requestEmail(): Promise<void> {
    if (this.emailBusy) return
    const errors: typeof this.emailErrors = {}
    const email = this.newEmail.trim().toLowerCase()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'Похоже, это не почта'
    else if (email === this.auth.user?.email) errors.email = 'Это текущая почта'
    this.emailErrors = errors
    if (Object.keys(errors).length > 0) return

    this.emailBusy = true
    try {
      await api.account.requestEmailChange(email)
      runInAction(() => { this.emailStep = 'code'; this.emailCode = '' })
    } catch (cause) {
      const message = explain(cause)
      runInAction(() => { this.emailErrors = { email: message } })
    } finally {
      runInAction(() => { this.emailBusy = false })
    }
  }

  /**
   * Подтверждение. Возвращает true, если почта сменилась.
   *
   * После смены бекенд гасит все токены, и текущая сессия мертва.
   * Экран обязан отправить человека на вход сам, а не оставить его
   * тыкать в кнопки, каждая из которых ответит «сессия истекла».
   */
  async confirmEmail(): Promise<boolean> {
    if (this.emailBusy) return false
    if (this.emailCode.trim() === '') {
      this.emailErrors = { code: 'Введите код из письма' }
      return false
    }
    this.emailBusy = true
    try {
      await api.account.confirmEmailChange(this.emailCode.trim())
      runInAction(() => { this.emailStep = 'idle' })
      this.ui.success('Почта изменена. Войдите заново')
      return true
    } catch (cause) {
      const message = explain(cause)
      runInAction(() => { this.emailErrors = { code: message } })
      return false
    } finally {
      runInAction(() => { this.emailBusy = false })
    }
  }

  // ================= часовой пояс =================

  /**
   * Пояс, по которому срабатывают автовыводы «по времени».
   *
   * Пока его нельзя было задать, правило «ежедневно в 12:00» уходило
   * с поясом браузера — того, из которого мерчант в последний раз
   * открыл кабинет. Уехав в отпуск, он получал вывод в другое время
   * суток и не понимал почему.
   */
  get currentTimezone(): string {
    return this.timezone || this.auth.user?.timezone || browserZone()
  }

  setTimezone(zone: string): void {
    this.timezone = zone
  }

  async saveTimezone(): Promise<void> {
    const zone = this.currentTimezone
    if (this.timezoneBusy || zone === this.auth.user?.timezone) return
    this.timezoneBusy = true
    try {
      await api.account.setTimezone(zone)
      await this.auth.refreshUser()
      this.ui.success('Часовой пояс сохранён')
    } catch (cause) {
      this.ui.failure(explain(cause))
    } finally {
      runInAction(() => { this.timezoneBusy = false })
    }
  }

  // ================= второй фактор =================

  setCode(value: string): void {
    // Резервные коды печатают строчными и с пробелами — приводим сами,
    // иначе человек трижды получит «код не подошёл» из-за регистра.
    this.code = value.toUpperCase().replace(/\s+/g, '')
    this.codeError = null
  }

  closeTwoFactor(): void {
    if (this.twoFactorBusy) return
    // Уйти с экрана резервных кодов, не подтвердив сохранение, нельзя:
    // второй раз их не покажут, и потерянный телефон закроет аккаунт.
    // Молча игнорировать нажатие тоже нельзя — человек решит, что
    // окно сломалось, и будет жать крестик, пока не закроет вкладку
    // вместе с кодами.
    if (this.twoFactor.kind === 'codes' && !this.codesSaved) {
      this.ui.failure('Сначала сохраните коды')
      return
    }
    this.twoFactor = { kind: 'idle' }
    this.code = ''
    this.codeError = null
    this.codesSaved = false
  }

  async startTwoFactor(): Promise<void> {
    if (this.twoFactorBusy) return
    this.twoFactorBusy = true
    try {
      const setup = await api.account.setupTwoFactor()
      runInAction(() => {
        this.twoFactor = { kind: 'scan', secret: setup.secret, uri: setup.otpauth_uri }
        this.code = ''
        this.codeError = null
      })
    } catch (cause) {
      this.ui.failure(explain(cause))
    } finally {
      runInAction(() => { this.twoFactorBusy = false })
    }
  }

  async enableTwoFactor(): Promise<void> {
    if (this.twoFactorBusy || this.twoFactor.kind !== 'scan') return
    if (this.code.length < 6) { this.codeError = 'Код из шести цифр'; return }
    this.twoFactorBusy = true
    try {
      const { codes } = await api.account.enableTwoFactor(this.code)
      await this.auth.refreshUser()
      runInAction(() => {
        this.twoFactor = { kind: 'codes', codes }
        this.codesSaved = false
        this.code = ''
      })
    } catch (cause) {
      const message = explain(cause)
      runInAction(() => { this.codeError = message })
    } finally {
      runInAction(() => { this.twoFactorBusy = false })
    }
  }

  askDisable(): void {
    this.twoFactor = { kind: 'disable' }
    this.code = ''
    this.codeError = null
  }

  async disableTwoFactor(): Promise<void> {
    if (this.twoFactorBusy || this.twoFactor.kind !== 'disable') return
    // Резервный код длиннее шести знаков и содержит буквы, а поле рядом
    // прямо предлагает его ввести. Отказ «код из шести цифр» закрыл бы
    // отключение 2FA как раз тому, кто потерял телефон.
    if (this.code.length < 6) {
      this.codeError = 'Неверный формат кода'
      return
    }
    this.twoFactorBusy = true
    try {
      await api.account.disableTwoFactor(this.code)
      await this.auth.refreshUser()
      runInAction(() => { this.twoFactor = { kind: 'idle' }; this.code = '' })
      this.ui.success('Двухфакторная аутентификация отключена')
    } catch (cause) {
      const message = explain(cause)
      runInAction(() => { this.codeError = message })
    } finally {
      runInAction(() => { this.twoFactorBusy = false })
    }
  }

  confirmCodesSaved(): void {
    this.codesSaved = true
  }

  /** Скачать резервные коды файлом. */
  downloadCodes(): void {
    if (this.twoFactor.kind !== 'codes') return
    const body = [
      'CryptoChangeK — резервные коды двухфакторной аутентификации',
      `Аккаунт: ${this.auth.user?.email ?? ''}`,
      '',
      'Каждый код срабатывает один раз. Храните файл там же,',
      'где храните пароли, — а не в переписке и не на рабочем столе.',
      '',
      ...this.twoFactor.codes,
      '',
    ].join('\n')
    const blob = new Blob([body], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'cryptochangek-backup-codes.txt'
    link.click()
    URL.revokeObjectURL(url)
    this.confirmCodesSaved()
  }
}

/** Пояс браузера. Годится как подсказка, но не как решение за человека. */
function browserZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/**
 * Пояса, которые предлагаются в списке.
 *
 * Полный перечень IANA — это шестьсот с лишним строк, и искать в нём
 * свой город человек будет дольше, чем настраивать сам автовывод.
 * Здесь — те, в которых работают мерчанты, плюс пояс браузера,
 * если его в списке нет.
 */
export const TIMEZONES = [
  'UTC',
  'Europe/Kaliningrad',
  'Europe/Moscow',
  'Europe/Samara',
  'Asia/Yekaterinburg',
  'Asia/Omsk',
  'Asia/Krasnoyarsk',
  'Asia/Irkutsk',
  'Asia/Yakutsk',
  'Asia/Vladivostok',
  'Asia/Magadan',
  'Asia/Kamchatka',
  'Europe/Kyiv',
  'Europe/Minsk',
  'Asia/Almaty',
  'Asia/Tbilisi',
  'Asia/Yerevan',
  'Asia/Baku',
  'Asia/Tashkent',
  'Asia/Dubai',
  'Asia/Bangkok',
  'Europe/Belgrade',
  'Europe/Berlin',
  'Europe/Lisbon',
] as const

/** «Europe/Moscow · UTC+3 · сейчас 21:40» — чтобы выбор был осмысленным. */
export function zoneLabel(zone: string): string {
  try {
    const now = new Date()
    const time = new Intl.DateTimeFormat('ru-RU', {
      timeZone: zone, hour: '2-digit', minute: '2-digit',
    }).format(now)
    const offset = new Intl.DateTimeFormat('en-US', {
      timeZone: zone, timeZoneName: 'shortOffset',
    }).formatToParts(now).find((p) => p.type === 'timeZoneName')?.value ?? ''
    return `${offset} · сейчас ${time}`
  } catch {
    return zone
  }
}
